import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  Activity,
  Award,
  CheckCircle2,
  ClipboardCheck,
  FileSearch,
  Gauge,
  Heart,
  History,
  ListChecks,
  Lock,
  PlayCircle,
  Printer,
  RefreshCw,
  TrendingDown,
  TrendingUp,
  XCircle,
} from 'lucide-react';
import { format } from 'date-fns';
import { api } from '../../api/client';
import StatCard from '../../components/StatCard';
import EmptyState from '../../components/EmptyState';
import PageHeader from '../../components/PageHeader';
import InfoTip from '../../components/InfoTip';
import ErrorBanner from '../../components/ErrorBanner';
import PillarFixCards from './audit/PillarFixCards';
import FixModal from './audit/FixModal';
import CsvExportButton from '../../components/CsvExportButton';
import { SkeletonHero, SkeletonGrid, SkeletonCard } from '../../components/Skeleton';
import { formatDate, formatDateTime } from '../../lib/format';

const HEALTH_LABELS = {
  botsAllowed: 'AI bots allowed in robots.txt',
  schemaPresent: 'JSON-LD schema detected',
  faqsStructured: 'FAQ sections present',
  llmsTxtPresent: 'llms.txt published',
  answerCapsulesUsed: 'Answer Capsules used',
  headingHierarchyCorrect: 'Heading hierarchy correct',
  indexableByCrawlers: 'Indexable by AI crawlers',
};

const PILLAR_META = {
  schemaMarkup: { label: 'Schema markup', color: '#d61f97' },
  faqStructure: { label: 'FAQ structure', color: '#ffa23d' },
  answerCapsules: { label: 'Answer capsules', color: '#5ac915' },
  headingHierarchy: { label: 'Heading hierarchy', color: '#ffd106' },
  botAccess: { label: 'Bot access', color: '#ff6a35' },
  llmsTxt: { label: 'llms.txt', color: '#f91880' },
  contentQuality: { label: 'Content quality', color: '#716b8b' },
};

const PILLAR_ORDER = [
  'schemaMarkup',
  'faqStructure',
  'answerCapsules',
  'headingHierarchy',
  'botAccess',
  'llmsTxt',
  'contentQuality',
];

const FALLBACK_PILLAR_MAX = {
  botAccess: 20,
  schemaMarkup: 20,
  faqStructure: 15,
  answerCapsules: 15,
  headingHierarchy: 15,
  llmsTxt: 10,
  contentQuality: 5,
};

const scoreTone = (score) =>
  score >= 75 ? 'green' : score >= 50 ? 'amber' : 'rose';
const scoreTextClass = (score) =>
  score >= 75 ? 'text-emerald-700' : score >= 50 ? 'text-amber-700' : 'text-rose-700';

export default function AuditTab({ domain }) {
  const qc = useQueryClient();
  const [activeFix, setActiveFix] = useState(null);
  const [fixesAppliedThisSession, setFixesAppliedThisSession] = useState(0);

  const auditQuery = useQuery({
    queryKey: ['audit-latest', domain._id],
    queryFn: () =>
      api
        .get(`/api/domains/${domain._id}/audit/latest`)
        .then((r) => r.data)
        .catch((err) => {
          if (err.response?.status === 404) return null;
          throw err;
        }),
  });

  const historyQuery = useQuery({
    queryKey: ['audit-score-history', domain._id],
    queryFn: () =>
      api.get(`/api/domains/${domain._id}/audit/score-history?limit=20`).then((r) => r.data),
  });

  const usageQuery = useQuery({
    queryKey: ['audit-usage'],
    queryFn: () => api.get(`/api/domains/${domain._id}/audit/usage`).then((r) => r.data),
  });

  const runMutation = useMutation({
    mutationFn: () =>
      api.post(`/api/domains/${domain._id}/audit`).then((r) => r.data.audit),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['audit-latest', domain._id] });
      qc.invalidateQueries({ queryKey: ['audit-score-history', domain._id] });
      qc.invalidateQueries({ queryKey: ['audit-usage'] });
    },
  });

  const data = auditQuery.data?.audit;
  const previous = auditQuery.data?.previous;
  const pillarMax = auditQuery.data?.pillarMax || FALLBACK_PILLAR_MAX;
  const usage = usageQuery.data?.audits;
  const quotaReached = usage && !usage.unlimited && usage.remaining === 0;

  const scoreDelta = useMemo(() => {
    if (!data || !previous?.avg_score) return null;
    return data.avg_score - previous.avg_score;
  }, [data, previous]);

  if (auditQuery.isLoading) {
    return (
      <div className="space-y-6">
        <SkeletonHero />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <SkeletonCard lines={5} />
          <SkeletonCard lines={5} />
        </div>
        <SkeletonGrid count={4} />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="space-y-6">
        <PageHeader
          variant="gradient"
          icon={ClipboardCheck}
          eyebrow="Audit"
          title="Site GEO scorecard"
          description="Run a deterministic audit that crawls your sitemap and grades every page from 0–100 on AI-citation readiness — schema, FAQs, Answer Capsules, heading hierarchy, bot access, llms.txt, and content quality."
          help={{
            title: 'How scoring works',
            body:
              'Each page is graded on 7 pillars worth a fixed number of points. The site score is the average across all crawled pages. This is rule-based — same inputs always produce the same score.',
          }}
        />
        {usage && <UsageBanner usage={usage} />}
        <EmptyState
          icon={ClipboardCheck}
          title="No audits yet"
          body="Run a GEO audit to score each page from 0-100 on AI-citation readiness. We crawl your sitemap and check schema, FAQs, headings, and llms.txt."
          action={
            <button
              className="btn-primary"
              onClick={() => runMutation.mutate()}
              disabled={runMutation.isPending || quotaReached}
            >
              {runMutation.isPending ? (
                <>
                  <Activity className="w-4 h-4 animate-pulse" /> Running audit (1-2 min)…
                </>
              ) : quotaReached ? (
                <>
                  <Lock className="w-4 h-4" /> Quota reached
                </>
              ) : (
                <>
                  <PlayCircle className="w-4 h-4" /> Run audit now
                </>
              )}
            </button>
          }
        />
      </div>
    );
  }

  return (
    <div className="space-y-6 print-scorecard">
      {fixesAppliedThisSession > 0 && !runMutation.isPending && (
        <div className="sticky top-2 z-30 rounded-xl border border-emerald-200 bg-emerald-50 shadow-sm px-4 py-3 flex items-center gap-3 print-hide">
          <span className="flex-none h-8 w-8 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center">
            <CheckCircle2 className="w-4 h-4" />
          </span>
          <div className="flex-1 text-sm">
            <div className="font-semibold text-emerald-900">
              {fixesAppliedThisSession} fix
              {fixesAppliedThisSession === 1 ? '' : 'es'} ready to verify
            </div>
            <div className="text-emerald-800/80 text-xs">
              Deploy your changes, then re-run the audit to see your new score.
            </div>
          </div>
          <button
            className="btn-primary flex-none"
            onClick={() =>
              runMutation.mutate(undefined, {
                onSuccess: () => setFixesAppliedThisSession(0),
              })
            }
            disabled={runMutation.isPending || quotaReached}
          >
            <RefreshCw className="w-4 h-4" />
            Re-audit now
          </button>
          <button
            type="button"
            onClick={() => setFixesAppliedThisSession(0)}
            className="btn-icon flex-none"
            aria-label="Dismiss"
          >
            <XCircle className="w-4 h-4" />
          </button>
        </div>
      )}

      <FixModal
        code={activeFix}
        domain={domain}
        audit={data}
        onClose={() => setActiveFix(null)}
        onFixApplied={() => setFixesAppliedThisSession((n) => n + 1)}
      />

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 print-hide">
        <div className="text-sm text-ink-400 flex items-center gap-2 flex-wrap">
          <ClipboardCheck className="w-4 h-4" />
          Last run:{' '}
          <span className="text-ink-700 font-medium">{formatDateTime(data.createdAt)}</span>
          {usage && !usage.unlimited && (
            <span className="pill-muted">
              <Gauge className="w-3 h-3" /> {usage.used}/{usage.limit} this month
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            className="btn-secondary"
            onClick={() => window.print()}
            title="Save as PDF via the browser print dialog"
          >
            <Printer className="w-4 h-4" /> Print scorecard
          </button>
          <button
            className="btn-secondary"
            onClick={() => runMutation.mutate()}
            disabled={runMutation.isPending || quotaReached}
          >
            {runMutation.isPending ? (
              <>
                <Activity className="w-4 h-4 animate-pulse" /> Running…
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4" /> Re-run audit
              </>
            )}
          </button>
        </div>
      </div>

      {/* Quota banner (e.g. you're 1 away from the limit, or already over) */}
      {usage && <UsageBanner usage={usage} className="print-hide" />}

      {runMutation.isError && (
        <ErrorBanner
          className="print-hide"
          title="Audit failed"
          body={runMutation.error?.response?.data?.error || 'Something went wrong while running the audit. Try again or check Settings → Anthropic API key.'}
          onDismiss={() => runMutation.reset()}
        />
      )}

      {/* Hero scorecard */}
      <ScorecardHero
        domain={domain}
        data={data}
        delta={scoreDelta}
        previous={previous}
      />

      {/* Pillar breakdown + score-over-time chart */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <PillarBreakdown pillars={data.pillars} pillarMax={pillarMax} previous={previous} />
        <ScoreOverTime history={historyQuery.data?.series} domainId={domain._id} />
      </div>

      {/* Top-line stat tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Site GEO score"
          value={`${data.avg_score}/100`}
          icon={Award}
          variant={scoreTone(data.avg_score)}
          info={{
            title: 'Site GEO score',
            body: 'Average of every crawled page\'s score across the 7 pillars. Aim for 75+ to be reliably quoted by AI engines.',
          }}
          trend={
            scoreDelta != null && scoreDelta !== 0
              ? {
                  value: `${scoreDelta > 0 ? '+' : ''}${scoreDelta} vs last`,
                  positive: scoreDelta > 0,
                }
              : null
          }
        />
        <StatCard
          label="Pages scanned"
          value={data.total_pages_scanned}
          icon={FileSearch}
          variant="brand"
          info={{
            title: 'Pages scanned',
            body: 'Pulled from your sitemap.xml. If this number looks low, check that your sitemap exists and is reachable.',
          }}
        />
        <StatCard
          label="Missing schema"
          value={data.missing_schema_count}
          icon={XCircle}
          variant="amber"
          info={{
            title: 'Missing JSON-LD schema',
            body: 'Pages without structured data. AI engines use schema to understand what an entity is (article, product, FAQ) — without it they often skip the page.',
          }}
        />
        <StatCard
          label="Missing FAQs"
          value={data.missing_faq_count}
          icon={XCircle}
          variant="rose"
          info={{
            title: 'Missing FAQ blocks',
            body: 'Pages with no Q&A section. FAQs are one of the highest-signal formats for AI citation — a well-placed FAQ can earn a quote in ChatGPT verbatim.',
          }}
        />
      </div>

      {/* What to fix — full-width, own container */}
      <PillarFixCards audit={data} onOpenFix={setActiveFix} />

      {/* Health checks + audit history side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <div className="section-header">
            <div className="section-title">
              <span className="section-icon">
                <Heart className="w-4 h-4" />
              </span>
              Site-wide health checks
            </div>
          </div>
          <ul className="text-sm divide-y divide-gray-50">
            {Object.entries(HEALTH_LABELS).map(([k, label]) => {
              const passed = data.health_checks?.[k];
              return (
                <li
                  key={k}
                  className="py-2.5 flex items-center justify-between first:pt-0 last:pb-0"
                >
                  <span className="text-ink-800">{label}</span>
                  {passed ? (
                    <span className="pill-success">
                      <CheckCircle2 className="w-3 h-3" /> Pass
                    </span>
                  ) : (
                    <span className="pill-danger">
                      <XCircle className="w-3 h-3" /> Fail
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>

        <AuditHistoryList domainId={domain._id} currentId={data._id} />
      </div>

      <div className="card">
        <div className="section-header">
          <div className="section-title">
            <span className="section-icon">
              <ListChecks className="w-4 h-4" />
            </span>
            Per-page scores
          </div>
        </div>
        <div className="overflow-x-auto scroll-soft">
          <table className="data-table">
            <thead>
              <tr>
                <th>URL</th>
                <th className="text-right">Score</th>
                <th className="text-right">Schema</th>
                <th className="text-right">FAQ</th>
                <th className="text-right">Capsules</th>
                <th className="text-right">Hierarchy</th>
              </tr>
            </thead>
            <tbody>
              {[...(data.page_scores || [])]
                .sort((a, b) => a.score - b.score)
                .slice(0, 50)
                .map((p) => (
                  <tr key={p.url}>
                    <td className="truncate max-w-[24rem] text-ink-800">{p.url}</td>
                    <td className={`text-right font-semibold ${scoreTextClass(p.score)}`}>
                      {p.score}
                    </td>
                    <td className="text-right text-ink-400">
                      {p.breakdown?.schemaMarkup}
                    </td>
                    <td className="text-right text-ink-400">
                      {p.breakdown?.faqStructure}
                    </td>
                    <td className="text-right text-ink-400">
                      {p.breakdown?.answerCapsules}
                    </td>
                    <td className="text-right text-ink-400">
                      {p.breakdown?.headingHierarchy}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ScorecardHero({ domain, data, delta, previous }) {
  const tone = scoreTone(data.avg_score);
  const ringColor =
    tone === 'green' ? '#10b981' : tone === 'amber' ? '#f59e0b' : '#f43f5e';
  // SVG ring: 100-step circumference visualization
  const radius = 56;
  const circumference = 2 * Math.PI * radius;
  const progress = Math.max(0, Math.min(100, data.avg_score));
  const offset = circumference * (1 - progress / 100);

  return (
    <div className="relative rounded-2xl overflow-hidden bg-ink-gradient text-white p-6 sm:p-8 shadow-xl">
      <div className="absolute inset-0 opacity-30 bg-brand-radial" />
      <div className="absolute inset-0 bg-grid opacity-20" />
      <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-brand-600 blur-3xl opacity-30" />
      <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
        <div className="flex items-center gap-5">
          <svg width="128" height="128" viewBox="0 0 128 128" className="flex-none">
            <circle
              cx="64"
              cy="64"
              r={radius}
              stroke="rgba(255,255,255,0.12)"
              strokeWidth="10"
              fill="none"
            />
            <circle
              cx="64"
              cy="64"
              r={radius}
              stroke={ringColor}
              strokeWidth="10"
              fill="none"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={offset}
              transform="rotate(-90 64 64)"
              style={{ transition: 'stroke-dashoffset 600ms ease-out' }}
            />
            <text
              x="64"
              y="60"
              textAnchor="middle"
              fontSize="32"
              fontWeight="700"
              fill="#fff"
            >
              {data.avg_score}
            </text>
            <text
              x="64"
              y="82"
              textAnchor="middle"
              fontSize="10"
              fill="rgba(255,255,255,0.6)"
              letterSpacing="1.5"
            >
              GEO SCORE
            </text>
          </svg>
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wider text-white/60 mb-1 flex items-center gap-1.5">
              <Award className="w-3 h-3" /> Site GEO scorecard
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold truncate">
              {domain.displayName || domain.domain}
            </h2>
            <div className="text-sm text-white/70 truncate">{domain.domain}</div>
            <div className="mt-2 text-xs text-white/60">
              {data.total_pages_scanned} pages scanned ·{' '}
              {formatDateTime(data.createdAt)}
            </div>
          </div>
        </div>
        <div className="flex flex-col items-start sm:items-end gap-2">
          {delta != null && delta !== 0 && (
            <div
              className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold ${
                delta > 0
                  ? 'bg-emerald-500/20 text-emerald-200'
                  : 'bg-rose-500/20 text-rose-200'
              }`}
            >
              {delta > 0 ? (
                <TrendingUp className="w-4 h-4" />
              ) : (
                <TrendingDown className="w-4 h-4" />
              )}
              {delta > 0 ? '+' : ''}
              {delta} vs previous
            </div>
          )}
          {previous && (
            <div className="text-xs text-white/60">
              Previous: {previous.avg_score}/100 on {formatDate(previous.createdAt)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PillarBreakdown({ pillars, pillarMax, previous }) {
  if (!pillars) {
    return (
      <div className="card">
        <div className="section-header">
          <div className="section-title">
            <span className="section-icon">
              <Gauge className="w-4 h-4" />
            </span>
            Pillar breakdown
          </div>
        </div>
        <p className="text-sm text-ink-400">No pillar data yet.</p>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="section-header">
        <div className="section-title">
          <span className="section-icon">
            <Gauge className="w-4 h-4" />
          </span>
          Pillar breakdown
        </div>
        <span className="text-xs text-ink-400">Avg per page</span>
      </div>
      <ul className="space-y-3">
        {PILLAR_ORDER.map((key) => {
          const meta = PILLAR_META[key];
          const value = pillars[key] || 0;
          const max = pillarMax[key] || 1;
          const pct = Math.min(100, Math.round((value / max) * 100));
          const prevValue = previous?.pillars?.[key];
          const diff = prevValue != null ? value - prevValue : null;
          return (
            <li key={key}>
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="font-medium text-ink-800 flex items-center gap-2">
                  <span
                    className="w-2.5 h-2.5 rounded-full"
                    style={{ backgroundColor: meta.color }}
                  />
                  {meta.label}
                </span>
                <span className="text-ink-400 inline-flex items-center gap-2">
                  {diff != null && diff !== 0 && (
                    <span
                      className={`text-[11px] font-semibold ${
                        diff > 0 ? 'text-emerald-600' : 'text-rose-600'
                      }`}
                    >
                      {diff > 0 ? '+' : ''}
                      {diff}
                    </span>
                  )}
                  <span className="text-ink-700 font-semibold">{value}</span>
                  <span>/ {max}</span>
                </span>
              </div>
              <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                <div
                  className="h-full rounded-full transition-all"
                  style={{ width: `${pct}%`, backgroundColor: meta.color }}
                />
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function ScoreOverTime({ history, domainId }) {
  const data = (history || []).map((s) => ({
    date: format(new Date(s.createdAt), 'MMM d'),
    score: s.avg_score,
    ...s.pillars,
  }));

  if (data.length < 2) {
    return (
      <div className="card">
        <div className="section-header">
          <div className="section-title">
            <span className="section-icon">
              <History className="w-4 h-4" />
            </span>
            Score over time
          </div>
        </div>
        <div className="flex items-center gap-2 text-sm text-ink-400">
          <History className="w-4 h-4" /> Run a second audit to start tracking your trend.
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="section-header">
        <div className="section-title">
          <span className="section-icon">
            <History className="w-4 h-4" />
          </span>
          Score over time
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-ink-400">{data.length} audits</span>
          {domainId && <CsvExportButton domainId={domainId} kind="audits" label="CSV" />}
        </div>
      </div>
      <ResponsiveContainer width="100%" height={260}>
        <AreaChart data={data}>
          <defs>
            <linearGradient id="scoreFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#d61f97" stopOpacity={0.35} />
              <stop offset="100%" stopColor="#d61f97" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
          <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#716b8b' }} />
          <YAxis
            domain={[0, 100]}
            tick={{ fontSize: 11, fill: '#716b8b' }}
            width={32}
          />
          <Tooltip
            contentStyle={{
              borderRadius: 12,
              border: '1px solid #e5e7eb',
              boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)',
            }}
          />
          <Area
            type="monotone"
            dataKey="score"
            stroke="#d61f97"
            strokeWidth={2.5}
            fill="url(#scoreFill)"
            name="Site score"
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function AuditHistoryList({ domainId, currentId }) {
  const { data } = useQuery({
    queryKey: ['audit-history', domainId],
    queryFn: () => api.get(`/api/domains/${domainId}/audit/history`).then((r) => r.data),
  });

  const audits = (data?.audits || []).slice(0, 10);
  if (audits.length < 2) return null;

  return (
    <div className="card">
      <div className="section-header">
        <div className="section-title">
          <span className="section-icon">
            <History className="w-4 h-4" />
          </span>
          Audit history
        </div>
        <span className="text-xs text-ink-400">Last {audits.length} runs</span>
      </div>
      <ul className="divide-y divide-gray-50">
        {audits.map((a) => {
          const isCurrent = a._id === currentId;
          return (
            <li
              key={a._id}
              className="py-2.5 flex items-center justify-between gap-3 text-sm first:pt-0 last:pb-0"
            >
              <div className="flex items-center gap-3 min-w-0">
                <span
                  className={`flex-none h-8 w-8 rounded-lg flex items-center justify-center font-bold ${
                    a.avg_score >= 75
                      ? 'bg-emerald-50 text-emerald-700'
                      : a.avg_score >= 50
                        ? 'bg-amber-50 text-amber-700'
                        : 'bg-rose-50 text-rose-600'
                  }`}
                >
                  {a.avg_score}
                </span>
                <div className="min-w-0">
                  <div className="text-ink-800">
                    {formatDateTime(a.createdAt)}{' '}
                    {isCurrent && (
                      <span className="pill-brand ml-1">Current</span>
                    )}
                  </div>
                  <div className="text-xs text-ink-400">
                    {a.total_pages_scanned} pages scanned
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function UsageBanner({ usage, className = '' }) {
  if (!usage || usage.unlimited) return null;
  const pct = (usage.used / usage.limit) * 100;
  const tone =
    usage.remaining === 0 ? 'rose' : pct >= 80 ? 'amber' : 'brand';
  const bg = {
    rose: 'bg-rose-50 border-rose-100 text-rose-700',
    amber: 'bg-amber-50 border-amber-100 text-amber-800',
    brand: 'bg-brand-50/60 border-brand-100 text-brand-800',
  }[tone];
  return (
    <div className={`rounded-xl border ${bg} text-sm p-3 flex items-center gap-2 ${className}`}>
      <Gauge className="w-4 h-4 flex-none" />
      <span className="font-medium">
        {usage.used}/{usage.limit} audits used this month.
      </span>
      <span className="text-current/70">
        {usage.remaining === 0
          ? 'Quota reached — re-runs are blocked until next month.'
          : `${usage.remaining} remaining on your plan.`}
      </span>
    </div>
  );
}
